// Columna izquierda del explorer de Vendedores: búsqueda · tienda · zona · rol.
// Los chips de zona y de rol salen de las filas, no de una lista fija.
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { Input, Select } from '@/kit';
import { cn } from '@/lib/cn';
import { NoSource } from '@/shared/patterns/NoSource';
import {
    countActiveFilters,
    EMPTY_SELLERS_FILTERS,
    roleOptions,
    zoneOptions,
    type SellerRow,
    type SellersFilters,
} from '../lib/sellersTable';

/** Centinela del select: Radix no admite `value=""` en un item. */
const ALL_STORES = 'ALL';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
const CHIP =
    'cursor-pointer whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold transition-colors';

export function SellersFiltersPanel({
    rows,
    filters,
    onChange,
    stores,
    collapsed,
    onToggleCollapsed,
    rbacAvailable,
}: {
    /** El universo de filas: de ahí salen los chips de zona. */
    rows: SellerRow[];
    filters: SellersFilters;
    onChange: (next: SellersFilters) => void;
    stores: { id: string; name: string }[];
    collapsed: boolean;
    onToggleCollapsed: () => void;
    /** Los chips de rol salen de RBAC; sin él no hay por cuál filtrar. */
    rbacAvailable: boolean;
}) {
    const patch = (partial: Partial<SellersFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const zones = zoneOptions(rows);
    const roles = roleOptions(rows);

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={() => {
                onChange(EMPTY_SELLERS_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <Input
                    type="search"
                    value={filters.query}
                    onChange={(e) => {
                        patch({ query: e.target.value });
                    }}
                    placeholder="Nombre, tel o clave"
                    aria-label="Buscar vendedor"
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Tienda</div>
                <Select
                    value={filters.storeId === '' ? ALL_STORES : filters.storeId}
                    onChange={(value) => {
                        patch({ storeId: value === ALL_STORES ? '' : value });
                    }}
                    options={[
                        { value: ALL_STORES, label: 'Todas' },
                        ...stores.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Zona</div>
                {zones.length === 0 ? (
                    <p className="text-[10px] leading-snug text-muted-foreground">
                        El agregado no devuelve la zona del vendedor, así que no hay por cuál
                        filtrar.
                    </p>
                ) : null}
                <div className="grid grid-cols-3 gap-1">
                    {zones.map((zone) => {
                        const on = filters.zones.includes(zone);
                        return (
                            <button
                                key={zone}
                                type="button"
                                aria-pressed={on}
                                onClick={() => {
                                    patch({
                                        zones: on
                                            ? filters.zones.filter((z) => z !== zone)
                                            : [...filters.zones, zone],
                                    });
                                }}
                                className={cn(
                                    CHIP,
                                    on
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-card hover:bg-accent',
                                )}
                            >
                                {zone}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Rol</div>
                {rbacAvailable ? null : (
                    <NoSource
                        reason="RBAC no respondió: no se pudieron leer los roles"
                        className="text-[10px]"
                    />
                )}
                <div className="flex flex-wrap gap-1">
                    {roles.map((role) => {
                        const on = filters.roleKeys.includes(role.key);
                        return (
                            <button
                                key={role.key}
                                type="button"
                                aria-pressed={on}
                                title={role.name}
                                onClick={() => {
                                    patch({
                                        roleKeys: on
                                            ? filters.roleKeys.filter((key) => key !== role.key)
                                            : [...filters.roleKeys, role.key],
                                    });
                                }}
                                className={cn(
                                    CHIP,
                                    'max-w-full truncate',
                                    on
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-card hover:bg-accent',
                                )}
                            >
                                {role.name}
                            </button>
                        );
                    })}
                </div>
            </div>
        </FiltersColumn>
    );
}
